import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import PublicChat from './pages/PublicChat';
import Onboarding from './pages/Onboarding';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import ServiceWorkerUpdateNotification from './components/ServiceWorkerUpdateNotification';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ConversationActivitiesProvider } from './contexts/ConversationActivitiesContext';
import { getAllRoutes, getDefaultRoute, isRouteAccessible } from './config/navigation';
import { registerServiceWorker } from './utils/serviceWorkerRegistration';

// Custom redirect component that checks authentication
const AuthRedirect = () => {
  const { isAuthenticated } = useAuth();
  const defaultRoute = getDefaultRoute();
  return isAuthenticated ? <Navigate to={defaultRoute} replace /> : <Navigate to="/login" replace />;
};

// Route guard component that checks module requirements
const RouteGuard = ({ children, path }) => {
  const { hasModule, clid } = useAuth();
  const location = useLocation();

  // Check if the current route is accessible
  const isAccessible = isRouteAccessible(location.pathname, hasModule, clid);

  if (!isAccessible) {
    return <Layout><NotFound /></Layout>;
  }

  return children;
};

// Protected routes component that has access to AuthContext
const ProtectedRoutes = () => {
  const { hasModule, clid } = useAuth();
  const routes = getAllRoutes(hasModule, clid);
  const defaultRoute = getDefaultRoute();

  return (
    <Routes>
      {/* Redirect root to default route */}
      <Route path="/" element={<Navigate to={defaultRoute} replace />} />

      {/* Dynamic routes from navigation config */}
      {routes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <RouteGuard path={route.path}>
              <Layout><route.component /></Layout>
            </RouteGuard>
          }
        />
      ))}

      {/* Catch-all route for 404 */}
      <Route path="*" element={<Layout><NotFound /></Layout>} />
    </Routes>
  );
};

function App() {
  useEffect(() => {
    // Register service worker on app mount
    registerServiceWorker();
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <ConversationActivitiesProvider>
          <ServiceWorkerUpdateNotification />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/chat" element={<PublicChat />} />
            <Route path="/onboarding" element={<Onboarding />} />

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/*" element={<ProtectedRoutes />} />
            </Route>

            {/* Fallback route - redirect to login if not authenticated */}
            <Route path="*" element={<AuthRedirect />} />
          </Routes>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#4ade80',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </ConversationActivitiesProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
